import React from 'react';
import './LegalPage.css';

function PrivacyPolicy() {
  return (
    <section className="legal-page">
      <div className="container">
        <div className="legal-content">
          <h1>Privacy Policy</h1>
          <span className="legal-date">Last updated: March 31, 2026</span>

          <p>
            This Privacy Policy describes how Budge ("we", "us", or "our") collects, uses, and protects
            your personal information when you use the Budge mobile application ("App"). By using our App,
            you agree to the collection and use of information in accordance with this policy.
          </p>

          <h2>1. Information We Collect</h2>

          <h3>1.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and profile photo (optional). You may also sign in via Google or Apple, in which case we receive basic profile information from those services.</li>
            <li><strong>Financial Data:</strong> Transaction details (amount, category, type, notes, date), EMI/loan information (loan name, lender, principal, interest rate, tenure), budget allocations, and savings goals that you manually enter into the App.</li>
            <li><strong>Profile Preferences:</strong> Monthly income, preferred currency, dark mode setting, and biometric lock preference.</li>
            <li><strong>Receipts:</strong> Photos of receipts you optionally upload for transaction records.</li>
          </ul>

          <h3>1.2 Information Collected Automatically</h3>
          <ul>
            <li><strong>Device Information:</strong> Device type, operating system version, and unique device identifiers for push notification delivery.</li>
            <li><strong>Push Notification Tokens:</strong> If you enable notifications, we collect your device's push notification token to send EMI reminders and budget alerts.</li>
          </ul>

          <h3>1.3 Information We Do NOT Collect</h3>
          <ul>
            <li>We do not collect bank account numbers, credit/debit card numbers, or banking credentials.</li>
            <li>We do not access your bank accounts or any external financial services.</li>
            <li>We do not collect location data.</li>
            <li>We do not collect browsing history or data from other apps.</li>
            <li>Biometric data (Face ID/fingerprint) is processed locally on your device and never transmitted to our servers.</li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide and maintain the App's core functionality (expense tracking, budgeting, EMI management, savings goals).</li>
            <li>Generate analytics, charts, and financial insights within the App.</li>
            <li>Send push notifications for EMI reminders and budget alerts (if enabled).</li>
            <li>Enable CSV export of your financial data.</li>
            <li>Authenticate your identity and secure your account.</li>
            <li>Improve and optimize the App.</li>
          </ul>

          <h2>3. Data Storage & Security</h2>
          <p>Your data is stored securely using Google Firebase services:</p>
          <ul>
            <li><strong>Firebase Authentication:</strong> Manages user authentication securely.</li>
            <li><strong>Cloud Firestore:</strong> Stores your financial data in encrypted form.</li>
            <li><strong>Firebase Storage:</strong> Stores uploaded receipt images.</li>
          </ul>
          <p>
            All data is transmitted using industry-standard TLS encryption. Your data is stored in Firebase's
            secure cloud infrastructure, which complies with SOC 1, SOC 2, and SOC 3 certifications.
          </p>
          <p>
            Biometric authentication data (Face ID/fingerprint) is handled entirely by your device's operating
            system and is never stored or transmitted by Budge.
          </p>

          <h2>4. Data Sharing</h2>
          <p>
            We do <strong>not</strong> sell, trade, or rent your personal information to third parties. We may
            share data only in the following circumstances:
          </p>
          <ul>
            <li><strong>Service Providers:</strong> We use Google Firebase for backend infrastructure. Firebase processes data in accordance with Google's privacy policies.</li>
            <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or governmental authority.</li>
            <li><strong>With Your Consent:</strong> We may share information when you explicitly give us permission (e.g., CSV export shared via your device's share sheet).</li>
          </ul>

          <h2>5. Data Retention</h2>
          <p>We retain your data for as long as your account is active. When you delete your account:</p>
          <ul>
            <li>All personal data, transactions, EMIs, budgets, savings goals, and uploaded receipts are permanently deleted.</li>
            <li>This deletion is irreversible and typically completes within 30 days.</li>
          </ul>

          <h2>6. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> View all your data within the App.</li>
            <li><strong>Export:</strong> Download your transaction data as a CSV file.</li>
            <li><strong>Delete:</strong> Delete your account and all associated data at any time from the Profile screen. See our <a href="/delete-account">Account Deletion</a> page for instructions.</li>
            <li><strong>Correct:</strong> Update your profile information at any time.</li>
            <li><strong>Opt-out:</strong> Disable push notifications through your device settings.</li>
          </ul>

          <h2>7. Children's Privacy</h2>
          <p>
            Budge is not intended for use by children under the age of 13. We do not knowingly collect personal
            information from children under 13. If we discover that a child under 13 has provided us with personal
            information, we will delete it immediately.
          </p>

          <h2>8. Third-Party Services</h2>
          <p>Our App uses the following third-party services:</p>
          <ul>
            <li><strong>Google Firebase</strong> (Authentication, Firestore, Storage)</li>
            <li><strong>Google Sign-In</strong> (Optional)</li>
            <li><strong>Apple Sign-In</strong> (Optional, iOS only)</li>
            <li><strong>Expo Push Notifications</strong> (Optional)</li>
          </ul>

          <h2>9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting
            the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review
            this Privacy Policy periodically.
          </p>

          <h2>10. Contact Us</h2>
          <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
          <p><strong>Email:</strong> <a href="mailto:support@budgeapp.com">support@budgeapp.com</a></p>
        </div>
      </div>
    </section>
  );
}

export default PrivacyPolicy;
